import { IImageSettings } from '@msdyn365-commerce/core';
import classnames from 'classnames';
import * as React from 'react';
import { ICipImageData } from './cip-image.data';
import { ICipImageProps } from './cip-image.props.autogenerated';

export interface ICipImageViewProps extends ICipImageProps<ICipImageData> {
    moduleClassName?: string;
    imageSettings?: IImageSettings;
}

/**
 * Hardcoded image configurations for different aspect ratios.
 * Only dimensions are hardcoded - mode, lazyload, and quality will be taken from Site Builder.
 */
const imageConfigurations: { [key: string]: IImageSettings } = {
    ratio1to1: {
        viewports: {
            xs: { w: 575, h: 575 },
            sm: { w: 708, h: 708 },
            md: { w: 932, h: 932 },
            lg: { w: 1380, h: 1380 },
            xl: { w: 1698, h: 1698 }
        },
        cropFocalRegion: true,
        mode: 5
    },
    ratio3to2: {
        viewports: {
            xs: { w: 575, h: 384 },
            sm: { w: 708, h: 472 },
            md: { w: 932, h: 622 },
            lg: { w: 1380, h: 920 },
            xl: { w: 1698, h: 1132 }
        },
        cropFocalRegion: true,
        mode: 5
    }
};

/**
 * Merges hardcoded dimensions with Site Builder configuration.
 * @param baseSettings - Base image settings with hardcoded dimensions.
 * @param siteBuilderSettings - Settings from Site Builder (quality, lazyload).
 * @returns Merged image settings.
 */
const mergeImageSettings = (baseSettings: IImageSettings, siteBuilderSettings: IImageSettings): IImageSettings => {
    const merged: IImageSettings = { ...baseSettings };

    // Only override if Site Builder has defined values
    if (siteBuilderSettings.lazyload !== undefined) {
        merged.lazyload = siteBuilderSettings.lazyload;
    }
    if (siteBuilderSettings.mode !== undefined) {
        merged.mode = siteBuilderSettings.mode;
    }
    if (siteBuilderSettings.quality !== undefined) {
        merged.quality = siteBuilderSettings.quality;
    }

    // Build dynamic query strings for each viewport with quality and mode
    const quality = merged.quality ?? 80;
    const mode = merged.mode ?? 5;

    if (merged.viewports) {
        Object.keys(merged.viewports).forEach(viewport => {
            const vp = merged.viewports[viewport as keyof typeof merged.viewports];
            if (vp && vp.w && vp.h) {
                vp.q = `w=${vp.w}&h=${vp.h}&q=${quality}&m=${mode}&f=webp&cropfocalregion=true`;
            }
        });
    }

    return merged;
};

const getImageSettingsForAspectRatio = (aspectRatio?: string, siteBuilderSettings?: IImageSettings): IImageSettings | undefined => {
    if (!aspectRatio || !siteBuilderSettings) {
        return undefined;
    }
    const baseSettings = imageConfigurations[aspectRatio as keyof typeof imageConfigurations];
    return baseSettings ? mergeImageSettings(baseSettings, siteBuilderSettings) : undefined;
};

export class CipImage extends React.PureComponent<ICipImageProps<{}>> {
    public render(): JSX.Element | null {
        const props = this.props;
        const { image, className } = props.config;
        const { imageAspectRatio } = props.config as typeof props.config & { imageAspectRatio?: string };

        if (!image?.src) {
            return null;
        }

        // Get image settings from the image field and merge with hardcoded dimensions
        // Image settings come from image.imageSettings (not image.props.imageSettings like in views)
        const imageSettings = getImageSettingsForAspectRatio(imageAspectRatio, image?.imageSettings);

        const cipImageViewProps = {
            ...props,
            moduleClassName: classnames('cip-image', className),
            imageSettings
        };

        return props.renderView(cipImageViewProps) as React.ReactElement;
    }
}

export default CipImage;
